import React, { Fragment, PropsWithChildren, useMemo, useState } from 'react'

import {
  ALL_POSSIBLE_ANSWERS,
  PrereqTo,
  StandalonePrereqProps,
} from './StandalonePrereq.types'
import {
  PrereqStep,
  usePendingSteps,
  useStepConfig,
  useSteps,
  useUpdatePrereqAnswers,
} from 'context/FlowPrerequirements'
import StandalonePrereqView, {
  KYCView,
  StandalonePrereqHeading,
} from './StandalonePrereq.view'
import { useUser } from 'context/User'
import SectionLoader from 'openspace/components/SectionLoader'
import { useGallery } from 'context/Gallery'
import { GalleryType } from 'core/logic/gallery/gallery.types'
import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import UncontrolledTransition from '@onepercentio/one-ui/components/UncontrolledTransition'
import { TransitionAnimationTypes } from '@onepercentio/one-ui/components/Transition'
import Styles from './StandalonePrereq.module.scss'
import Text from 'openspace/components/Text'
import Container from 'openspace/components/Container'
import Card from 'components/Card'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import { useHistory } from 'react-router-dom'
import { PrerequirementsConfigs } from '@onepercentio/rarum-types/_shared'
import { resetKYC, startKYC } from 'core/modules/firebase/service/user'
import { t, tO } from 'translate/i18n'
import { useScrollWhenEffect } from 'containers/MainRouter/MainRouter.hook'
import ProgressDispatcher from 'openspace/service/Progress'

function PendingPrereqForm({
  children,
  to,
  gallery,
  shouldRequestDataConsent,
  onBack,
}: PropsWithChildren<
  {
    to: PrereqTo
    gallery: GalleryType
  } & Pick<StandalonePrereqProps, 'shouldRequestDataConsent' | 'onBack'>
>) {
  const pendingSteps = usePendingSteps(
    shouldRequestDataConsent ? gallery : undefined
  )
  // At some moment, the form will be uploaded and the screen should be shown
  const [complete, setIsComplete] = useState(() => pendingSteps.length === 0)
  const [currStep, setCurrStep] = useState(pendingSteps[0])
  const config = useStepConfig(currStep)
  const { detail, profile } = useUser()
  const [answers, setAnswers] = useState<ALL_POSSIBLE_ANSWERS>({})
  const { goBack } = useHistory()
  const { updateProfile, setAcceptDataUsage } = useUpdatePrereqAnswers(gallery)

  const backAction = onBack || goBack

  const acceptDataUsage = shouldRequestDataConsent
    ? (answers: ALL_POSSIBLE_ANSWERS) =>
        setAcceptDataUsage(answers.confirmedDataSharing![0])
    : () => {}

  const savePrereq = useAsyncControl({
    updateProfile: async (
      stepsToUpdate: PrereqStep[],
      answers: ALL_POSSIBLE_ANSWERS
    ) => {
      await acceptDataUsage(answers)
      await updateProfile(stepsToUpdate, answers)
      setIsComplete(true)
    },
    updateProfileWithKYC: async (
      stepsToUpdate: PrereqStep[],
      answers: ALL_POSSIBLE_ANSWERS,
      kycMethod: PrerequirementsConfigs['kyc']
    ) => {
      await acceptDataUsage(answers)
      await updateProfile(stepsToUpdate, answers)
      ProgressDispatcher.dispatchHandle(
        'kyc',
        t('flowRequirements.steps.kyc.progress.sendingData'),
        0
      )
      await startKYC(kycMethod)
    },
  })

  useScrollWhenEffect(currStep)

  const screen = () => {
    if (complete) return <Fragment key='child'>{children}</Fragment>
    if (currStep === PrereqStep.KYC) {
      const kycConfig = config as PrerequirementsConfigs['kyc']
      return (
        <KYCView
          key='kyc'
          method={kycConfig}
          onCancel={backAction}
          onConfirmed={() => setIsComplete(true)}
          onRetry={() => {
            resetKYC(kycConfig).then(() => {
              setCurrStep(pendingSteps[0])
            })
          }}
          onStarted={() =>
            savePrereq.updateProfileWithKYC(pendingSteps, answers, kycConfig)
          }
          error={savePrereq.error}
          loading={savePrereq.loading}
          profile={profile!}
        />
      )
    }
    if (savePrereq.loading)
      return (
        <Fragment key='loading'>
          <Container size={800}>
            <Card>
              <Spacing size='40' />
              <SectionLoader />
              <center>
                <Text
                  code={`flowRequirements.standalone.to.${to}.loading`}
                  type='h3'
                />
              </center>
              <Spacing size='40' />
            </Card>
          </Container>
        </Fragment>
      )
    return (
      <StandalonePrereqView
        key='prereq'
        prereqTo={to}
        currStep={currStep}
        config={config}
        username={detail?.displayName!}
        gallery={gallery}
        steps={pendingSteps}
        storedAnswers={answers}
        onCancel={() => {
          if (currStep === pendingSteps[0]) backAction()
          else
            setCurrStep((prev) => pendingSteps[pendingSteps.indexOf(prev) - 1])
        }}
        onBack={() => backAction()}
        onConfirm={(ans) => {
          const updatedAnswers = { ...answers, ...ans }
          setAnswers(() => updatedAnswers)
          if (currStep !== pendingSteps[pendingSteps.length - 1])
            setCurrStep((prev) => pendingSteps[pendingSteps.indexOf(prev) + 1])
          else savePrereq.updateProfile(pendingSteps, updatedAnswers)
        }}
        shouldRequestDataConsent={shouldRequestDataConsent}
        heading={
          <StandalonePrereqHeadingLogic
            prereqTo={to}
            username={detail?.displayName!}
          />
        }
      />
    )
  }

  return (
    <UncontrolledTransition
      transitionType={TransitionAnimationTypes.FADE}
      className={Styles.root}
      contentClassName={Styles.content}>
      {screen()}
    </UncontrolledTransition>
  )
}

export default function StandalonePrereqLogic(props: StandalonePrereqProps) {
  const prereqSteps = useSteps()
  const gallery = useGallery(props.galleryId)
  const { profile } = useUser()

  const screen = () => {
    if (
      prereqSteps === undefined ||
      (prereqSteps.length && (!gallery || !profile || !profile.isLoaded))
    )
      return <SectionLoader key={'loading'} />
    if (prereqSteps.length)
      return (
        <PendingPrereqForm
          key={'prereq'}
          {...props}
          gallery={gallery!}
          to={props.prereqTo}>
          <>{props.children}</>
        </PendingPrereqForm>
      )
    return <Fragment key={'child'}>{props.children}</Fragment>
  }

  return (
    <>
      <UncontrolledTransition
        transitionType={TransitionAnimationTypes.FADE}
        className={Styles.root}
        contentClassName={Styles.content}>
        {screen()}
      </UncontrolledTransition>
      <Spacing size='40' />
    </>
  )
}

function StandalonePrereqHeadingLogic(props: {
  username: string
  prereqTo: PrereqTo
}) {
  const prereqConfig = useMemo(() => {
    switch (props.prereqTo) {
      case 'claim':
        return {
          title: tO('flowRequirements.standalone.hi', {
            name: props.username,
          }),
          description: tO(
            `flowRequirements.standalone.to.${props.prereqTo}.title`
          ),
        }
      case 'marketplace-buy':
      case 'marketplace-sell':
        return {
          title: t('marketplace.offerCreation.sections.kyc.title'),
          description: tO(
            `flowRequirements.standalone.to.${props.prereqTo}.title`
          ),
        }
    }
  }, [props.prereqTo])
  return <StandalonePrereqHeading {...prereqConfig} />
}
